// MYAC-1522 (onboarding pass, decisions H and E) — the one true action a person
// is offered where they have no project to look at: the empty dashboard and the
// empty QMS / product / watch lists. Decided by the role and the plan only, so
// that every empty screen says the same thing: create what the plan allows,
// otherwise explain it is not in the plan and lead to the team, and never offer
// a member (not an admin) a create button their role cannot use.
import { ProjectCreationRequestType } from '@/models/Billing'

export type FirstStep =
  /** An admin whose plan allows this type. `alsoUpgrade`: the plan has no QMS, say so beside it. */
  | { kind: 'create'; type: ProjectCreationRequestType; alsoUpgrade: boolean }
  /** An admin whose plan does not allow it (`type` null: nothing can be created at all). */
  | { kind: 'upgrade'; type: ProjectCreationRequestType | null }
  /** A member: only an admin can share or create a project. */
  | { kind: 'ask_admin' }
  /** An admin outside projects the company already has: join them (Paramètres › QMS, TD et veille). */
  | { kind: 'join' }

/** What the dashboard leans toward first, in this order. */
const DASHBOARD_ORDER = [
  ProjectCreationRequestType.QMS,
  ProjectCreationRequestType.TD,
  ProjectCreationRequestType.REGULATORY_MONITORING,
] as const

export function firstStep(opts: {
  isAdmin: boolean
  canCreate: (type: ProjectCreationRequestType) => boolean
  /** The list the screen is about; null on the dashboard. */
  scope: ProjectCreationRequestType | null
  /** How many projects of the company (of `scope`, when set) this admin is not a member of. */
  outsideProjects?: number
}): FirstStep {
  if (!opts.isAdmin) return { kind: 'ask_admin' }
  // An admin invited before access came with the invitation sees nothing while the
  // company has projects: offering to create one would duplicate them.
  if ((opts.outsideProjects ?? 0) > 0) return { kind: 'join' }
  if (opts.scope) {
    return opts.canCreate(opts.scope)
      ? { kind: 'create', type: opts.scope, alsoUpgrade: false }
      : { kind: 'upgrade', type: opts.scope }
  }
  const type = DASHBOARD_ORDER.find((t) => opts.canCreate(t))
  if (!type) return { kind: 'upgrade', type: null }
  return { kind: 'create', type, alsoUpgrade: !opts.canCreate(ProjectCreationRequestType.QMS) }
}

/** The list a route base stands for (`ProjectsGrid`'s `routeBase`). */
export function scopeOfRouteBase(routeBase: string): ProjectCreationRequestType | null {
  if (routeBase.startsWith('/qms')) return ProjectCreationRequestType.QMS
  if (routeBase.startsWith('/products')) return ProjectCreationRequestType.TD
  if (routeBase.startsWith('/regulatory-monitoring')) return ProjectCreationRequestType.REGULATORY_MONITORING
  return null
}
